import { Fragment, type ComponentProps, type ReactNode } from 'react';
import { parseComposerReferences, type ComposerSegment } from '@runloop/reflex-contract';
import { useContentScanners } from '../../client/content-scanners';
import { MarkdownContent } from './MarkdownContent';
import { renderContentScannerAnchor } from './markdown/content-scanner-dispatch';
import { AgentRunBadge } from '../shared/AgentRunBadge';
import { ContextRefBadge } from '../shared/ContextRefBadge';
import { contextRefTarget } from '../shared/context-ref-links';

type Props = ComponentProps<typeof MarkdownContent>;

/**
 * One referenced context item in a message: the plugin's own renderer when it
 * has one, the {@link ContextRefBadge} otherwise.
 *
 * A pull request is the case that matters. Pasting its URL into the composer
 * turns it into a reference, which the chat used to render as a bare, dead
 * chip — the link the author pasted was gone, and the PR's state with it. So a
 * reference whose kind has a richer renderer is promoted to its canonical URL
 * and dispatched through the `url` content-scanner registry, exactly as the
 * same link written in prose is: the GitHub plugin answers with its stateful
 * PR chip and hover card. Every other kind (and any kind whose plugin is
 * disabled) falls back to the badge, now carrying the link.
 */
function ContextRefSegment({ kind, id, title }: { kind: string; id: string; title: string }) {
  const target = contextRefTarget(kind, id);
  const badge = <ContextRefBadge contextKind={kind} refId={id} title={title} href={target?.url} />;
  if (!target?.scan) return badge;
  return <ScannedContextRef url={target.url} title={title} fallback={badge} />;
}

/**
 * A reference dispatched through the `url` scanner registry. Split out so only
 * the kinds that can be promoted subscribe to the registry — a transcript full
 * of Linear and Sentry chips doesn't re-render when a plugin loads.
 */
function ScannedContextRef({
  url,
  title,
  fallback,
}: {
  url: string;
  title: string;
  fallback: ReactNode;
}) {
  // Subscribe so a late-registering plugin scanner re-renders this in place.
  useContentScanners();
  return <>{renderContentScannerAnchor(url, title, () => fallback)}</>;
}

/**
 * Group parsed segments into "paragraphs" — runs of inline content separated
 * by blank-line boundaries in text segments. Within a paragraph, text and
 * chips flow inline; between paragraphs, normal block spacing applies.
 */
function textSegment(value: string): ComposerSegment {
  return { type: 'text', value } satisfies Extract<ComposerSegment, { type: 'text' }>;
}

function groupIntoParagraphs(segments: ComposerSegment[]): ComposerSegment[][] {
  const paragraphs: ComposerSegment[][] = [[]];
  for (const seg of segments) {
    if (seg.type !== 'text') {
      paragraphs[paragraphs.length - 1].push(seg);
      continue;
    }
    const parts = seg.value.split(/\n\n+/);
    for (let i = 0; i < parts.length; i++) {
      if (i > 0) paragraphs.push([]);
      if (parts[i]) {
        paragraphs[paragraphs.length - 1].push(textSegment(parts[i]));
      }
    }
  }
  return paragraphs.filter((p) => p.length > 0);
}

function renderSegment(
  seg: ComposerSegment,
  i: number,
  rest: Omit<Props, 'content'>,
  inline: boolean,
): ReactNode {
  if (seg.type === 'text') {
    if (!seg.value.trim()) return inline ? <Fragment key={i}> </Fragment> : null;
    if (inline) {
      const inlineClass = [rest.className?.trim(), 'contents [&>p]:inline']
        .filter(Boolean)
        .join(' ');
      // Each text segment is parsed as its own Markdown document. Markdown
      // trims whitespace at a document boundary, so preserve the authored
      // boundary spaces outside that document when it sits beside a chip.
      return (
        <Fragment key={i}>
          {/^\s/.test(seg.value) ? ' ' : null}
          <MarkdownContent content={seg.value} {...rest} className={inlineClass} />
          {/\s$/.test(seg.value) ? ' ' : null}
        </Fragment>
      );
    }
    return <MarkdownContent key={i} content={seg.value} {...rest} />;
  }
  if (seg.type === 'context') {
    return <ContextRefSegment key={i} kind={seg.kind} id={seg.id} title={seg.title} />;
  }
  return <AgentRunBadge key={i} agentId={seg.id} title={seg.title} />;
}

function renderParagraph(
  segments: ComposerSegment[],
  rest: Omit<Props, 'content'>,
  key: number,
): ReactNode {
  const hasChips = segments.some((s) => s.type !== 'text');
  if (!hasChips && segments.length === 1 && segments[0].type === 'text') {
    return segments[0].value.trim() ? (
      <MarkdownContent key={key} content={segments[0].value} {...rest} />
    ) : null;
  }
  return (
    <div key={key} className="inline">
      {segments.map((seg, i) => renderSegment(seg, i, rest, hasChips))}
    </div>
  );
}

/**
 * Renders message text like {@link MarkdownContent}, but collapses any
 * `<referenced-agent-run>` block into a compact {@link AgentRunBadge} and any
 * `<referenced-context>` block (a referenced PR, ticket, issue, …) into a
 * {@link ContextRefSegment} — each with a details popover — instead of dumping
 * the raw expanded context. Falls back to plain markdown when the content
 * holds no such blocks, so the common path is unchanged.
 */
export function MarkdownWithAgentRefs({ content, ...rest }: Props) {
  const segments = parseComposerReferences(content);
  if (segments.length === 1 && segments[0].type === 'text') {
    return <MarkdownContent content={content} {...rest} />;
  }

  const paragraphs = groupIntoParagraphs(segments);

  if (paragraphs.length === 1) {
    return renderParagraph(paragraphs[0], rest, 0);
  }

  return (
    <div className="flex flex-col items-start gap-1.5">
      {paragraphs.map((para, i) => renderParagraph(para, rest, i))}
    </div>
  );
}
